<script setup>
import { useStore } from 'vuex'
import TitleAlertSaveImport from '@/components/common/TitleAlertSaveImport.vue'
import HydraulicHeadBoundaryConditionsTable from '@/components/boundary-condition/HydraulicHeadBoundaryConditionsTable.vue'
import ImportFileStepper from '@/components/common/ImportFileStepper.vue'
import DownloadDialog from '@/components/common/DownloadDialog.vue'
import BoundaryConditionWarning from '@/components/boundary-condition/BoundaryConditionWarning.vue'
import BoundaryConditionAlert from '@/components/boundary-condition/BoundaryConditionAlert.vue'
import { boundaryConditionMixin } from '@/mixins/boundaryConditionMixin'
import { computed, onMounted, ref } from 'vue'

const store = useStore()

const STORE_NAMESPACE = 'hydraulicHeadBoundaryCondition'
const hydraulicHeadBoundaryCondition = ref(null)

const {
  dialog,
  options,
  downloadDialog,
  exportDialog,
  writeLoading,
  fetchLoading,
  count,
  downloadLoading,
  exportLoading,
  items,
  importItems,
  ignoredReferencePoint,
  importItemCount,
  loading,
  templateFileName,
  exportFileName,
  years,
  importedYears,
  addNewItem,
  fetchYears,
  fetchAvailableReferencePoints,
  fetchOptions,
  changePaginatedPage,
  downloadTemplate,
  exportData,
  saveChanges
} = boundaryConditionMixin(STORE_NAMESPACE, hydraulicHeadBoundaryCondition)

onMounted(async () => {
  fetchLoading.value = true
  await fetchYears()
  await fetchAvailableReferencePoints()
  await fetchOptions()
  fetchLoading.value = false
})

const lineCoords = computed(() => store.state.hydraulicHeadBoundaryCondition.lineCoords)
const importLineCoords = computed(() => store.state.hydraulicHeadBoundaryCondition.importLineCoords)
const deleteWarning = computed(() => store.state.hydraulicHeadBoundaryCondition.deleteWarning)
const resetItemState = () => store.dispatch('hydraulicHeadBoundaryCondition/resetItemState')
const fetchSingleLine = () => store.dispatch('hydraulicHeadBoundaryCondition/fetchSingleLine')
const handleResetItemState = () => {
  if (deleteWarning.value) {
    resetItemState()
  }
}
onMounted(async () => {
  fetchLoading.value = true
  await fetchYears()
  await fetchAvailableReferencePoints()
  await fetchSingleLine()
  await fetchOptions()
  fetchLoading.value = false
})
</script>

<template>
  <v-card style="min-height: 8px" :loading="loading" variant="flat" rounded="0">
    <template #loader="{ isActive }">
      <v-progress-linear :active="isActive" indeterminate color="primary" class="mx-1" />
    </template>
    <BoundaryConditionAlert
      v-if="!loading && !count"
      type="hydraulic head"
      @import="dialog = true"
      @download="downloadDialog = true"
    />
    <div v-else>
      <HydraulicHeadBoundaryConditionsTable
        v-if="options"
        ref="hydraulicHeadBoundaryCondition"
        @changePage="changePaginatedPage"
        :items="items"
        :count="count"
        :years="years"
        :lineCoords="lineCoords"
      >
        <template v-slot>
          <TitleAlertSaveImport
            v-if="!loading"
            @add="addNewItem"
            export-text="data export"
            import-text="data import"
            download-text="template"
            @save="saveChanges()"
            @import="dialog = true"
            :loading-save="writeLoading"
            :download-button="true"
            @download="downloadDialog = true"
            :export-button="true"
            @export="exportDialog = true"
          />
        </template>
      </HydraulicHeadBoundaryConditionsTable>
    </div>
    <v-dialog v-model="dialog" max-width="85%">
      <ImportFileStepper
        @close-dialog="dialog = false"
        :storeNamespace="STORE_NAMESPACE"
        text="Hydraulic head boundary conditions"
        @resetItemState="handleResetItemState()"
      >
        <template v-slot>
          <BoundaryConditionWarning
            :ignored-reference-point="ignoredReferencePoint"
            :import-warning="deleteWarning"
          />
          <HydraulicHeadBoundaryConditionsTable
            :items="importItems"
            :count="importItemCount"
            :years="importedYears"
            :editable="false"
            :lineCoords="importLineCoords"
          />
        </template>
      </ImportFileStepper>
    </v-dialog>
    <DownloadDialog
      @update:dialog="downloadDialog = $event"
      :dialog="downloadDialog"
      width="500"
      :file-name="templateFileName"
      :loading="downloadLoading"
      text="Download hydraulic head template"
      @download="downloadTemplate()"
    ></DownloadDialog>
    <DownloadDialog
      @update:dialog="exportDialog = $event"
      :dialog="exportDialog"
      width="500"
      :file-name="exportFileName"
      :loading="exportLoading"
      text="Export hydraulic head data"
      @download="exportData()"
    ></DownloadDialog>
  </v-card>
</template>
